import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { of, Subscription } from 'rxjs';
import { AjaxError } from 'rxjs/ajax';
import { catchError, finalize } from 'rxjs/operators';

import { HttpCodes } from 'app/constants/http-codes';
import { RestfulModel } from 'app/model';
import { RestfulApiService } from 'app/services';
import { ModelWithoutId } from 'app/type';
import { clearResolvedValue, selectResolvedValue, setResolvedValue } from 'features/resolver/slice';

interface HookReturnType<T extends RestfulModel<number | string>> {
    /**
     * The loaded entity or undefined if bad or no id;
     */
    entity: T | undefined;

    /**
     * The loading status.
     */
    loading: boolean;

    /**
     * A way to update the loaded model if using directly in forms.
     *
     * @param model
     */
    update: (model: ModelWithoutId<T>) => void;

    /**
     * A function which will trigger a reload entity.
     */
    reload: () => void;
}

/**
 * @param service a RestfulService for loading the model by id
 * @param entityId an Id of an entity to resolve
 * @param parent optional parent if the model requires it
 */
export const useEntityResolver2 = <T extends RestfulModel<number | string>, P = never>(
    service: RestfulApiService<T, P>,
    entityId: number | string | undefined,
    parent?: P,
): HookReturnType<T> => {
    const [loading, setLoading] = useState(false);
    const [sequence, setSequence] = useState(0);
    const dispatch = useDispatch();

    const loaded = useSelector(selectResolvedValue<T>(service.constructor.name, entityId));

    const update = useCallback(
        (model: ModelWithoutId<T>) => {
            dispatch(setResolvedValue({ data: model, name: service.constructor.name }));
        },
        [dispatch, service],
    );

    const reload = useCallback(() => {
        dispatch(clearResolvedValue(service.constructor.name));
        setSequence((current) => current + 1);
    }, [dispatch, service]);

    useEffect(() => {
        let subscription: Subscription | undefined;
        if (String(loaded?.id) === String(entityId)) {
            // if id already loaded, do nothing
            return undefined;
        }
        if ('new' !== entityId && entityId !== undefined) {
            setLoading(true);
            subscription = service
                .get(entityId, parent)
                .pipe(
                    finalize(() => setLoading(false)),
                    catchError((err: AjaxError) => {
                        if (err.status === HttpCodes.NOT_FOUND.valueOf()) {
                            return of(loaded);
                        }
                        throw err;
                    }),
                )
                .subscribe((data) => {
                    dispatch(setResolvedValue({ data, name: service.constructor.name }));
                });
        }

        return () => subscription?.unsubscribe();
    }, [dispatch, entityId, loaded, parent, service, sequence]);

    return { entity: loaded, loading, update, reload };
};
